import { newSpecPage } from '@stencil/core/testing';
import { NylasAvailabilityPicker } from '../nylas-availability-picker';
import { h } from '@stencil/core';
import { SelectDropdown } from '../../../design-system/select-dropdown/select-dropdown';
import { selectedConfiguration } from '@/utils/test-utils';
import { LANGUAGE_CODE } from '@/common/constants';
import { TimeInput } from '../../nylas-time-window-picker/nylas-time-window-picker';
describe('nylas-availability-picker', () => {
  const config = selectedConfiguration;

  beforeEach(() => {
    const originalIntl = Intl;

    global.Intl = {
      ...originalIntl,
      DateTimeFormat: jest.fn().mockImplementation(() => ({
        resolvedOptions: () => ({
          timeZone: 'America/New_York',
        }),
        supportedLocalesOf: jest.fn(),
      })) as any,
    };
  });

  afterEach(() => {
    jest.restoreAllMocks();
  });

  it('renders', async () => {
    const openHours = [
      {
        days: [1, 2, 3, 4, 5],
        exdates: null,
        timezone: 'America/Toronto',
        start: '09:00',
        end: '17:00',
      },
    ];

    const page = await newSpecPage({
      components: [NylasAvailabilityPicker],
      template: () => {
        return (
          <form id="availability-picker">
            <nylas-availability-picker name="availability" openHours={openHours}></nylas-availability-picker>
          </form>
        );
      },
    });

    expect(page.root).toMatchSnapshot();
  });

  it('renders based on the openHours prop', async () => {
    const openHours = [
      {
        days: [1],
        exdates: null,
        timezone: 'America/Toronto',
        start: '09:00',
        end: '17:00',
      },
    ];

    const page = await newSpecPage({
      components: [NylasAvailabilityPicker],
      template: () => {
        return (
          <form id="availability-picker">
            <nylas-availability-picker name="availability" openHours={openHours}></nylas-availability-picker>
          </form>
        );
      },
    });

    expect(page.root).toMatchSnapshot();
  });

  it('shows overlap error', async () => {
    const page = await newSpecPage({
      components: [NylasAvailabilityPicker],
      template: () => {
        return (
          <form id="availability-picker">
            <nylas-availability-picker name="availability"></nylas-availability-picker>
          </form>
        );
      },
    });
    const element = page.rootInstance as NylasAvailabilityPicker;

    const newSchedule = {
      SUN: [],
      MON: [
        { start: '09:00am', end: '05:00pm' },
        { start: '10:00am', end: '06:00pm' },
      ],
      TUE: [],
      WED: [],
      THU: [],
      FRI: [],
      SAT: [],
    };
    element.scheduleChanged(newSchedule);
    await page.waitForChanges();
    expect(page.root).toMatchSnapshot();
    const days = await page.root?.shadowRoot?.querySelectorAll('.availability-day');
    expect(days).not.toBeNull();
    const monday = days?.[1];
    expect(monday).not.toBeNull();
    const error = await monday?.querySelector('.error');
    expect(error).not.toBeNull();
    expect(error?.textContent).toEqual('Overlapping time ranges');
  });

  it('selects default timezone that is passed', async () => {
    const newConfig = JSON.parse(JSON.stringify(config));
    newConfig.event_booking.timezone = 'America/Vancouver';
    newConfig.availability.availability_rules.default_open_hours[0].timezone = 'America/Vancouver';
    const page = await newSpecPage({
      components: [NylasAvailabilityPicker, SelectDropdown],
      template: () => {
        return (
          <form id="availability-picker">
            <nylas-availability-picker name="availability" selectedConfiguration={newConfig} defaultTimezone="America/Vancouver"></nylas-availability-picker>
          </form>
        );
      },
    });
    await page.waitForChanges();
    const timezoneDropdown = page.root?.shadowRoot?.querySelector('.select-timezone select-dropdown')?.shadowRoot;
    const timezoneButton = timezoneDropdown?.querySelector('button');
    expect(timezoneButton).not.toBeNull();
    expect(timezoneButton?.textContent).toEqual('America / Vancouver');
  });

  it('selects browser timezone is timezone is set to empty string in the config', async () => {
    const newConfig = JSON.parse(JSON.stringify(config));
    newConfig.event_booking.timezone = '';
    newConfig.availability.availability_rules.default_open_hours[0].timezone = 'America/Vancouver';
    const page = await newSpecPage({
      components: [NylasAvailabilityPicker, SelectDropdown],
      template: () => {
        return (
          <form id="availability-picker">
            <nylas-availability-picker name="availability" selectedConfiguration={newConfig} defaultTimezone="America/Vancouver"></nylas-availability-picker>
          </form>
        );
      },
    });
    await page.waitForChanges();
    const timezoneDropdown = page.root?.shadowRoot?.querySelector('.select-timezone select-dropdown')?.shadowRoot;
    const timezoneButton = timezoneDropdown?.querySelector('button');
    expect(timezoneButton).not.toBeNull();
    expect(timezoneButton?.textContent).toEqual('America / Vancouver');
  });

  it('selects browser timezone if defaultTimezone is not passed', async () => {
    const config = selectedConfiguration as any;
    const page = await newSpecPage({
      components: [NylasAvailabilityPicker, SelectDropdown],
      template: () => {
        return (
          <form id="availability-picker">
            <nylas-availability-picker name="availability" selectedConfiguration={config}></nylas-availability-picker>
          </form>
        );
      },
    });

    await page.waitForChanges();
    const timezoneDropdown = page.root?.shadowRoot?.querySelector('.select-timezone select-dropdown')?.shadowRoot;
    const timezoneButton = timezoneDropdown?.querySelector('button');
    expect(timezoneButton).not.toBeNull();
    expect(timezoneButton?.textContent).toEqual('America / Toronto');
  });

  it('selects event_booking.timezone', async () => {
    const config = JSON.parse(JSON.stringify(selectedConfiguration));
    config.event_booking.timezone = 'Africa/Accra';
    const page = await newSpecPage({
      components: [NylasAvailabilityPicker, SelectDropdown],
      template: () => {
        return (
          <form id="availability-picker">
            <nylas-availability-picker name="availability" selectedConfiguration={config}></nylas-availability-picker>
          </form>
        );
      },
    });

    await page.waitForChanges();
    const timezoneDropdown = page.root?.shadowRoot?.querySelector('.select-timezone select-dropdown')?.shadowRoot;
    const timezoneButton = timezoneDropdown?.querySelector('button');
    expect(timezoneButton).not.toBeNull();
    expect(timezoneButton?.textContent).toEqual('Africa / Accra');
  });

  it('shows time in 24 hour format if language is not en', async () => {
    const page = await newSpecPage({
      components: [NylasAvailabilityPicker, TimeInput],
      template: () => {
        return (
          <form id="availability-picker">
            <nylas-availability-picker name="availability" selectedLanguage={LANGUAGE_CODE.de}></nylas-availability-picker>
          </form>
        );
      },
    });
    await page.waitForChanges();
    expect(page.root).toMatchSnapshot();
  });
});
